import { useState, useCallback, useRef, useEffect } from 'react';

export type ReminderTargetType = 'event' | 'contest' | 'live' | 'program';

export interface Reminder {
  id: string;
  targetId: number | string;
  targetType: ReminderTargetType;
  targetTitle: string;
  targetDate: string;
  createdAt: number;
}

function loadReminders(): Reminder[] {
  try {
    const raw = localStorage.getItem('rfw_reminders');
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveReminders(reminders: Reminder[]) {
  localStorage.setItem('rfw_reminders', JSON.stringify(reminders));
}

export function useReminders() {
  const [reminders, setReminders] = useState<Reminder[]>(() => loadReminders());
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Cleanup expired reminders (past dates)
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      const now = new Date();
      setReminders((prev) => {
        const filtered = prev.filter((r) => new Date(r.targetDate) >= now);
        if (filtered.length !== prev.length) {
          saveReminders(filtered);
        }
        return filtered;
      });
    }, 30000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const addReminder = useCallback(
    (targetId: number | string, targetType: ReminderTargetType, targetTitle: string, targetDate: string) => {
      setReminders((prev) => {
        const exists = prev.find(
          (r) => r.targetId === targetId && r.targetType === targetType
        );
        if (exists) return prev;
        const newReminder: Reminder = {
          id: `${targetType}-${targetId}-${Date.now()}`,
          targetId,
          targetType,
          targetTitle,
          targetDate,
          createdAt: Date.now(),
        };
        const next = [...prev, newReminder];
        saveReminders(next);
        return next;
      });
    }
  );

  const removeReminder = useCallback(
    (targetId: number | string, targetType: ReminderTargetType) => {
      setReminders((prev) => {
        const next = prev.filter(
          (r) => !(r.targetId === targetId && r.targetType === targetType)
        );
        saveReminders(next);
        return next;
      });
    }
  );

  const hasReminder = useCallback(
    (targetId: number | string, targetType: ReminderTargetType) => {
      return reminders.some(
        (r) => r.targetId === targetId && r.targetType === targetType
      );
    },
    [reminders]
  );

  const getRemindersByType = useCallback(
    (type: ReminderTargetType) => reminders.filter((r) => r.targetType === type),
    [reminders]
  );

  const toggleReminder = useCallback(
    (targetId: number | string, targetType: ReminderTargetType, targetTitle: string, targetDate: string) => {
      if (hasReminder(targetId, targetType)) {
        removeReminder(targetId, targetType);
        return false;
      } else {
        addReminder(targetId, targetType, targetTitle, targetDate);
        return true;
      }
    },
    [hasReminder, removeReminder, addReminder]
  );

  return {
    reminders,
    addReminder,
    removeReminder,
    hasReminder,
    getRemindersByType,
    toggleReminder,
  };
}